import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  AlertTriangle, 
  XCircle,
  Loader2,
  ArrowLeft,
  Search,
  Clock,
  Files,
  User,
  Folder
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Document types matching the case documents panel
// Default fallback document types (used if API fetch fails)
const DEFAULT_DOCUMENT_TYPES = [
  { value: 'assessment', label: 'Assessment' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'identification', label: 'Identification' },
  { value: 'individual_employment_plan', label: 'Individual Employment Plan' },
  { value: 'paystub', label: 'Paystub' },
  { value: 'referral_direct', label: 'Referral - Direct' },
  { value: 'referral_reverse', label: 'Referral - Reverse' },
  { value: 'timesheet', label: 'Timesheet' },
  { value: 'verification', label: 'Verification' },
  { value: 'other', label: 'Other' }
];

const MassDocumentUploadPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Configuration state
  const [programTypes, setProgramTypes] = useState([]);
  const [documentTypes, setDocumentTypes] = useState(DEFAULT_DOCUMENT_TYPES);
  const [selectedProgramType, setSelectedProgramType] = useState('');
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  
  // Upload state
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState('');
  
  // Results state
  const [results, setResults] = useState(null);
  const [exceptionsQueue, setExceptionsQueue] = useState([]);
  
  // Exception resolution dialog
  const [showExceptionDialog, setShowExceptionDialog] = useState(false);
  const [selectedExceptionIndex, setSelectedExceptionIndex] = useState(null);
  const [participantSearch, setParticipantSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantCases, setParticipantCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState('');
  const [resolving, setResolving] = useState(false);

  // Check access
  const hasAccess = ['system_admin', 'executive', 'operations_manager'].includes(user?.role);

  useEffect(() => {
    if (hasAccess) {
      fetchProgramTypes();
      fetchDocumentTypes();
    }
  }, [hasAccess]);

  const fetchProgramTypes = async () => {
    try {
      const response = await axios.get(`${API}/admin/program-types`);
      // Handle both array and object responses
      const types = Array.isArray(response.data) ? response.data : (response.data.program_types || response.data.types || []);
      setProgramTypes(types.filter(pt => pt.is_active !== false));
    } catch (error) {
      console.error('Failed to fetch program types:', error);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      // Primary: fetch from unified dropdowns API
      const response = await axios.get(`${API}/dropdowns/document_types`);
      if (response.data.items && response.data.items.length > 0) {
        setDocumentTypes(response.data.items);
      }
    } catch (error) {
      console.error('Failed to fetch document types from dropdowns API:', error);
      // Fallback: try the legacy admin/document-types endpoint
      try {
        const legacyResponse = await axios.get(`${API}/admin/document-types`);
        if (legacyResponse.data.types && legacyResponse.data.types.length > 0) {
          // Convert legacy format to dropdown format
          const converted = legacyResponse.data.types
            .filter(t => t.is_active !== false)
            .map(t => ({ value: t.id, label: t.name }));
          setDocumentTypes(converted);
        }
      } catch (legacyError) {
        console.error('Failed to fetch document types from legacy API:', legacyError);
        // Keep default types if all fetches fail
      }
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFiles(selectedFiles);
    setResults(null);
    setExceptionsQueue([]);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    setFiles(droppedFiles);
    setResults(null);
    setExceptionsQueue([]);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const processUpload = async () => {
    if (!selectedProgramType || !selectedDocumentType) {
      toast.error('Please select both Program Type and Document Type');
      return;
    }
    
    if (files.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setProcessingStatus('Preparing files...');
    setResults(null);
    setExceptionsQueue([]);

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      formData.append('program_type', selectedProgramType);
      formData.append('document_type', selectedDocumentType);

      setProcessingStatus(`Uploading ${files.length} files...`);
      setUploadProgress(20);

      const response = await axios.post(`${API}/admin/mass-upload/process`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 300000, // 5 minute timeout for large batches
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 50) / progressEvent.total);
          setUploadProgress(20 + percentCompleted);
        }
      });

      setUploadProgress(100);
      setProcessingStatus('Processing complete!');
      setResults(response.data);
      setExceptionsQueue(response.data.exceptions_queue || []);

      if (response.data.successful > 0) {
        toast.success(`Successfully attached ${response.data.successful} document(s)`);
      }
      if (response.data.exceptions > 0) {
        toast.warning(`${response.data.exceptions} document(s) require manual review`);
      }
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} document(s) failed to process`);
      }

    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process upload');
    } finally {
      setUploading(false);
    }
  };

  const openExceptionDialog = (index) => {
    setSelectedExceptionIndex(index);
    setParticipantSearch('');
    setSearchResults([]);
    setSelectedParticipant(null);
    setParticipantCases([]);
    setSelectedCase('');
    setShowExceptionDialog(true);
  };

  const searchParticipants = async () => {
    if (!participantSearch || participantSearch.length < 2) return;
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/admin/mass-upload/participant-search`, {
        params: { query: participantSearch }
      });
      setSearchResults(response.data.participants || []);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const selectParticipant = async (participant) => {
    setSelectedParticipant(participant);
    setSelectedCase('');
    
    try {
      const response = await axios.get(`${API}/admin/mass-upload/participant-cases/${participant.id}`);
      setParticipantCases(response.data.cases || []);
    } catch (error) {
      console.error('Failed to fetch cases:', error);
      setParticipantCases([]);
    }
  };

  const resolveException = async () => {
    if (!selectedParticipant || !selectedCase) {
      toast.error('Please select a participant and case');
      return;
    }

    const exception = exceptionsQueue[selectedExceptionIndex];
    
    setResolving(true);
    try {
      await axios.post(`${API}/admin/mass-upload/resolve-exception`, {
        temp_doc_id: exception.temp_doc_id,
        temp_storage_path: exception.temp_storage_path,
        participant_id: selectedParticipant.id,
        case_id: selectedCase,
        document_type: selectedDocumentType,
        original_filename: exception.original_filename
      });

      toast.success('Document attached successfully');
      
      // Remove from exceptions queue
      const updatedQueue = [...exceptionsQueue];
      updatedQueue.splice(selectedExceptionIndex, 1);
      setExceptionsQueue(updatedQueue);
      
      // Update results count
      if (results) {
        setResults({
          ...results,
          successful: results.successful + 1,
          exceptions: results.exceptions - 1
        });
      }
      
      setShowExceptionDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve exception');
    } finally {
      setResolving(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
            <h3 className="text-lg font-medium">Access Denied</h3>
            <p className="text-muted-foreground mt-2">
              You don&apos;t have permission to access this feature.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mass-upload-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/settings')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Settings
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Mass Document Upload</h1>
        <p className="text-muted-foreground mt-1">
          Upload multiple participant documents at once. Files should be named: &quot;Document Type - Participant Name.pdf&quot;
        </p>
      </div>

      {/* Configuration */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle className="text-lg">Upload Configuration</CardTitle>
          <CardDescription>
            Select the program and document type for all files in this batch
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="program-type">Enrollment/Case Type *</Label>
              <Select 
                value={selectedProgramType} 
                onValueChange={setSelectedProgramType}
                disabled={uploading}
              >
                <SelectTrigger id="program-type" data-testid="program-type-select">
                  <SelectValue placeholder="Select enrollment/case type" />
                </SelectTrigger>
                <SelectContent>
                  {programTypes.map(pt => (
                    <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="document-type">Document Type *</Label>
              <Select 
                value={selectedDocumentType} 
                onValueChange={setSelectedDocumentType}
                disabled={uploading}
              >
                <SelectTrigger id="document-type" data-testid="document-type-select">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map(dt => (
                    <SelectItem key={dt.value} value={dt.value}>
                      {dt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* File Upload Area */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle className="text-lg">Select Files</CardTitle>
          <CardDescription>
            Drag and drop files or click to browse. Supported formats: PDF, JPG, PNG, DOC, DOCX
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              uploading ? 'bg-muted/50 cursor-not-allowed' : 'hover:border-primary hover:bg-muted/50 cursor-pointer'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => !uploading && document.getElementById('file-input')?.click()}
          >
            <input
              id="file-input"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              className="hidden"
              onChange={handleFileSelect}
              disabled={uploading}
            />
            <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium">
              {files.length > 0 ? `${files.length} file(s) selected` : 'Drop files here or click to browse'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Filename format: &quot;Document Type - Participant Name.pdf&quot;
            </p>
          </div>

          {/* Selected Files List */}
          {files.length > 0 && !results && (
            <div className="mt-4 max-h-60 overflow-y-auto">
              <Label className="text-sm text-muted-foreground mb-2 block">
                Selected Files ({files.length})
              </Label>
              <div className="space-y-1">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-muted rounded text-sm">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span className="flex-1 truncate">{file.name}</span>
                    <span className="text-muted-foreground">{formatFileSize(file.size)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Progress Indicator */}
          {uploading && (
            <div className="mt-6 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {processingStatus}
                </span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}

          {/* Upload Button */}
          {!results && (
            <Button
              className="mt-6 w-full"
              size="lg"
              onClick={processUpload}
              disabled={uploading || files.length === 0 || !selectedProgramType || !selectedDocumentType}
              data-testid="process-upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Process {files.length} File{files.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Results Summary */}
      {results && (
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Processing Complete
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="text-center p-4 bg-muted rounded-lg">
                <Files className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{results.total_files}</p>
                <p className="text-xs text-muted-foreground">Total Files</p>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-600" />
                <p className="text-2xl font-bold text-green-700">{results.successful}</p>
                <p className="text-xs text-muted-foreground">Successful</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-yellow-600" />
                <p className="text-2xl font-bold text-yellow-700">{exceptionsQueue.length}</p>
                <p className="text-xs text-muted-foreground">Exceptions</p>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-950 rounded-lg">
                <XCircle className="w-6 h-6 mx-auto mb-2 text-red-600" />
                <p className="text-2xl font-bold text-red-700">{results.failed}</p>
                <p className="text-xs text-muted-foreground">Failed</p>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <Clock className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{results.processing_time_seconds}s</p>
                <p className="text-xs text-muted-foreground">Processing Time</p>
              </div>
            </div>

            <Button 
              variant="outline" 
              onClick={() => {
                setFiles([]);
                setResults(null);
                setExceptionsQueue([]);
              }}
            >
              Upload More Files
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Exceptions Queue */}
      {exceptionsQueue.length > 0 && (
        <Card className="card-shadow border-yellow-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              Exceptions Queue ({exceptionsQueue.length})
            </CardTitle>
            <CardDescription>
              These documents could not be automatically matched. Click to manually assign them.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Filename</TableHead>
                  <TableHead>Extracted Name</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exceptionsQueue.map((exception, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="truncate max-w-[200px]">{exception.original_filename}</span>
                      </div>
                    </TableCell>
                    <TableCell>{exception.extracted_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-yellow-700 border-yellow-300">
                        {exception.error}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        size="sm" 
                        onClick={() => openExceptionDialog(index)}
                        data-testid={`resolve-exception-${index}`}
                      >
                        Resolve
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Exception Resolution Dialog */}
      <Dialog open={showExceptionDialog} onOpenChange={setShowExceptionDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Resolve Exception</DialogTitle>
            <DialogDescription>
              {selectedExceptionIndex !== null && exceptionsQueue[selectedExceptionIndex] && (
                <>Manually assign &quot;{exceptionsQueue[selectedExceptionIndex].original_filename}&quot; to a participant</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Participant Search */}
            <div className="space-y-2">
              <Label>Search Participant</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter participant name..."
                  value={participantSearch}
                  onChange={(e) => setParticipantSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchParticipants()}
                />
                <Button onClick={searchParticipants} disabled={searching}>
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-2">
                <Label>Select Participant</Label>
                <div className="max-h-40 overflow-y-auto border rounded-lg">
                  {searchResults.map((p) => (
                    <button
                      key={p.id}
                      className={`w-full text-left px-3 py-2 hover:bg-muted flex items-center gap-2 ${
                        selectedParticipant?.id === p.id ? 'bg-primary/10' : ''
                      }`}
                      onClick={() => selectParticipant(p)}
                    >
                      <User className="w-4 h-4 text-muted-foreground" />
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Case Selection */}
            {selectedParticipant && (
              <div className="space-y-2">
                <Label>Select Case</Label>
                {participantCases.length > 0 ? (
                  <Select value={selectedCase} onValueChange={setSelectedCase}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a case" />
                    </SelectTrigger>
                    <SelectContent>
                      {participantCases.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          <div className="flex items-center gap-2">
                            <Folder className="w-4 h-4" />
                            {c.case_number || c.id} - {c.program_type_name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-muted-foreground p-3 bg-muted rounded">
                    No active cases found for this participant
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExceptionDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={resolveException} 
              disabled={!selectedParticipant || !selectedCase || resolving}
            >
              {resolving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Attaching...
                </>
              ) : (
                'Attach Document'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MassDocumentUploadPage;
